import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { api, clearSession } from '../api';

type NavItem = {
  label: string;
  to?: string;          // internal admin SPA route
  href?: string;        // external (cross-domain) URL — full reload
  match?: string[];     // path prefixes that mark this item active
};

// Same six-item structure as workers/tempo/src/portal.ts portalShell()
// so users see identical chrome whether they're on
// cadenceadvisers.com/portal/* (Worker-rendered) or
// admin.cadenceadvisers.com (this SPA, post-redirect).
//
// Engagements lives in the portal Worker, not the admin SPA — that link
// crosses subdomains. The other five live in the SPA.
const NAV: NavItem[] = [
  { label: 'Sessions',    to: '/queue',                                            match: ['/queue', '/review'] },
  { label: 'Engagements', href: 'https://cadenceadvisers.com/portal/engagements' },
  { label: 'Delivered',   to: '/delivered',                                        match: ['/delivered'] },
  { label: 'Audit',       to: '/audit',                                            match: ['/audit'] },
  { label: 'Compositions', to: '/compositions',                                     match: ['/compositions'] },
  { label: 'Settings',     to: '/settings',                                         match: ['/settings'] },
  { label: 'Demo',         to: '/demo',                                             match: ['/demo'] },
  { label: 'Architecture', to: '/architecture',                                     match: ['/architecture'] },
];

// Brand palette — must stay in sync with workers/tempo/src/brand-tokens.ts
// and the portalShell CSS in portal.ts. Inlined here because the admin
// SPA's Tailwind config has its own (different) color names.
const NAVY = '#142849';
const NAVY_DEEP = '#0d1c33';
const GOLD = '#c9a961';
const CREAM_TEXT = '#f5ecd7';
const PAPER = '#fdfaf2';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (!cancelled) setEmail(me.email);
      } catch (err: any) {
        if (cancelled) return;
        if (err.status === 401) {
          clearSession();
          navigate('/login', { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  function isActive(item: NavItem): boolean {
    if (!item.match) return false;
    return item.match.some(prefix =>
      location.pathname === prefix || location.pathname.startsWith(prefix + '/')
    );
  }

  const userInitial = (email || 'U').charAt(0).toUpperCase();

  const navStyle: CSSProperties = {
    background: NAVY,
    color: CREAM_TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 48px',
    borderBottom: `1px solid ${NAVY_DEEP}`,
    flexWrap: 'wrap',
    gap: 12,
    fontFamily: "'Newsreader', Georgia, serif",
  };

  const linkBase: CSSProperties = {
    color: CREAM_TEXT,
    textDecoration: 'none',
    fontFamily: "'Newsreader', Georgia, serif",
    fontSize: 15,
    paddingBottom: 2,
    borderBottom: '2px solid transparent',
    opacity: 0.95,
  };
  const linkActive: CSSProperties = {
    color: GOLD,
    opacity: 1,
    borderBottomColor: GOLD,
  };

  return (
    <div style={{ minHeight: '100vh', background: PAPER }}>
      <nav style={navStyle}>
        <a
          href="https://cadenceadvisers.com/portal"
          aria-label="Cadence Advisers — portal home"
          style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <img
            src="https://cadenceadvisers.com/brand/logo/cadence-advisers-full-cream.svg"
            alt="Cadence Advisers"
            style={{ height: 72, width: 'auto', display: 'block' }}
          />
        </a>
        <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          {NAV.map(item => {
            const active = isActive(item);
            const style = { ...linkBase, ...(active ? linkActive : {}) };
            if (item.href) {
              return (
                <a key={item.label} href={item.href} style={style}>
                  {item.label}
                </a>
              );
            }
            return (
              <NavLink key={item.label} to={item.to!} style={style}>
                {item.label}
              </NavLink>
            );
          })}
          <span style={{ width: 1, height: 18, background: 'rgba(245,236,215,0.18)', margin: '0 4px' }} />
          {email && (
            <span
              style={{
                fontFamily: "'JetBrains Mono', Menlo, monospace",
                fontSize: 12,
                color: CREAM_TEXT,
                opacity: 0.9,
                letterSpacing: '0.05em',
              }}
            >
              {email}
            </span>
          )}
          <span
            title={email || ''}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: `1px solid ${GOLD}`,
              fontFamily: "'JetBrains Mono', Menlo, monospace",
              fontSize: 13,
              fontWeight: 500,
              color: GOLD,
              textTransform: 'uppercase',
            }}
          >
            {userInitial}
          </span>
          {/* Sign out submits to the portal-domain logout endpoint so the
           * portal cookie (Domain=.cadenceadvisers.com) gets cleared in
           * the same round-trip. Local admin Bearer cleared first via the
           * onSubmit handler. */}
          <form
            method="POST"
            action="https://cadenceadvisers.com/portal/logout"
            onSubmit={() => {
              api.logout().catch(() => { /* ignore */ });
              clearSession();
            }}
            style={{ display: 'inline', margin: 0, padding: 0 }}
          >
            <button
              type="submit"
              style={{
                background: 'none',
                border: 'none',
                color: CREAM_TEXT,
                opacity: 0.9,
                fontFamily: "'Newsreader', Georgia, serif",
                fontSize: 14,
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Sign out
            </button>
          </form>
        </div>
      </nav>

      <main className="px-4 md:px-10 py-8 md:py-12 max-w-[1100px] mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
